import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PipelineStage } from '../../hooks/usePipelines';
import type { Deal } from '../../hooks/useDeals';
import { DealCard } from './DealCard';
import { cn } from '../../lib/cn';

interface PipelineBoardProps {
  stages: PipelineStage[];
  deals: Deal[];
  onDealClick: (deal: Deal) => void;
  onMoveStage: (dealId: string, newStageId: string) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

export function PipelineBoard({ stages, deals, onDealClick, onMoveStage }: PipelineBoardProps) {
  const dealsByStage = new Map<string, Deal[]>();
  for (const stage of stages) {
    dealsByStage.set(stage.id, []);
  }
  for (const deal of deals) {
    const list = dealsByStage.get(deal.stageId);
    if (list) {
      list.push(deal);
    }
  }
  // Sort each column by updatedAt desc
  for (const list of dealsByStage.values()) {
    list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {stages.map((stage, stageIndex) => {
        const stageDeals = dealsByStage.get(stage.id) ?? [];
        const totalPremium = stageDeals.reduce((sum, d) => sum + (d.premium ?? 0), 0);
        const prevStage = stageIndex > 0 ? stages[stageIndex - 1] : null;
        const nextStage = stageIndex < stages.length - 1 ? stages[stageIndex + 1] : null;

        return (
          <div
            key={stage.id}
            className="flex w-72 shrink-0 flex-col rounded-lg bg-surface-50 border border-surface-200"
          >
            {/* Column Header */}
            <div className="border-b border-surface-200 px-3 py-2.5">
              <div className="flex items-center gap-2">
                {stage.color && (
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                )}
                <h3 className="text-sm font-semibold text-slate-800">{stage.name}</h3>
                <span className="ml-auto rounded-full bg-surface-200 px-1.5 py-0.5 text-2xs font-medium text-slate-600">
                  {stageDeals.length}
                </span>
              </div>
              {totalPremium > 0 && (
                <p className="mt-0.5 text-xs text-slate-400">
                  {formatCurrency(totalPremium)}
                </p>
              )}
            </div>

            {/* Cards */}
            <div className="flex-1 space-y-2 overflow-y-auto p-2" style={{ maxHeight: '60vh' }}>
              {stageDeals.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">No deals</div>
              ) : (
                stageDeals.map((deal) => (
                  <div key={deal.id}>
                    <DealCard deal={deal} onClick={onDealClick} />
                    {/* Move stage buttons */}
                    <div className="mt-1 flex justify-center gap-1">
                      {prevStage && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveStage(deal.id, prevStage.id);
                          }}
                          className="rounded p-0.5 text-slate-400 hover:bg-surface-200 hover:text-slate-600"
                          title={`Move to ${prevStage.name}`}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {nextStage && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveStage(deal.id, nextStage.id);
                          }}
                          className="rounded p-0.5 text-slate-400 hover:bg-surface-200 hover:text-slate-600"
                          title={`Move to ${nextStage.name}`}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
